// @ts-nocheck

import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import * as Flags from '../../assets/img/flags';
import Formatter from '../../i18n/formatter';
import { __ } from '../../i18n/l10n';
import theme from '../../styled-components/dark';
import { withOpacity } from '../../styled-components/dark/helpers';
import FlagIcon from '../FlagIcon';
import SportIcon from '../SportIcon';

const Wrapper = styled.div`
  width: 100%;
  padding: 0 0.8em 0 0.8em;
  line-height: 1.25em;
  border-radius: inherit;
  box-shadow: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const RowWrapper = styled.div`
  width: 100%;
  padding: 0.8em 0 0.8em 0;

  display: flex;
  align-items: center;
  justify-content: space-between;

  ${({ $border }) =>
    $border
      ? css`
          border-top: 1px solid ${withOpacity(theme.SearchResults.color, '0c')};
        `
      : undefined}
`;

const ResultWrapper = styled.div``;

const ResultLiveWrapper = styled.div`
  background: ${theme.SearchResults.liveMatch.backgroundColor};
  border-radius: 8px;
  color: ${theme.SearchResults.liveMatch.labelColor};
  font-weight: bold;
  padding: 4px 8px;
  margin-left: 5px;
  text-transform: uppercase;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  white-space: break-spaces;
  overflow: hidden;
  color: ${theme.DefaultSearchRowResult.rowColor};

  &.row-meta {
    align-items: flex-start;
  }
`;

const StyledFlagIcon = styled(FlagIcon)`
  width: 1.5em;
  height: 1.5em;
  margin-right: 0.5em;
`;

const StyledSportIcon = styled(SportIcon)`
  width: 2em;
  height: 2em;
  ${({ $isLast }) =>
    $isLast
      ? css`
          margin-right: 0.5em;
        `
      : css`
          margin-right: 0.33em;
        `}
`;

const Title = styled.div`
  color: ${theme.DefaultSearchRowResult.titleColor};
`;

const FirstTitle = styled.span``;

const SecondTitle = styled.span`
  font-weight: bold;
`;

const StartDate = styled.div`
  font-size: 0.8em;
  color: ${theme.DefaultSearchRowResult.startDateColor};
  white-space: nowrap;

  &:first-letter {
    text-transform: uppercase;
  }
`;

const Separator = styled.div`
  font-size: 1.3em;
  color: ${theme.DefaultSearchRowResult.separatorColor};
  margin: 0em 0.2em;
`;

const Breadcrumbs = styled.div`
  font-size: 0.8em;
  color: ${theme.DefaultSearchRowResult.breadcrumbsColor};

  display: flex;
  align-items: center;

  & > div {
    font-size: 80%;
    margin-right: 0.4em;
  }
`;

type PropsType = {
  flagId?: string;
  isLive?: boolean;
  sportId: number;
  secondTitle?: string;
  startDate?: number;
  subtitleNames: Array<string>;
  subtitleFlag?: string;
  title: string;
  border?: boolean;
};

const SearchDefaultSearchRowResult = ({
  flagId,
  isLive,
  secondTitle,
  startDate,
  sportId,
  subtitleNames,
  subtitleFlag,
  title,
  border = false,
}: PropsType) => {
  const shouldDisplay = useMemo(() => !!title, [title]);
  const shouldDisplayFlagIcon = useMemo(() => !!flagId && !!~Object.keys(Flags).indexOf(flagId), [flagId]);

  const formattedStartDate = useMemo(() => (startDate ? Formatter.formatCalendar(startDate) : null), [startDate]);
  const shouldDisplayStartDate = useMemo(() => !!formattedStartDate, [formattedStartDate]);

  const shouldDisplayBreadcrumbs = useMemo(() => !!subtitleNames.length, [subtitleNames]);
  const breadcrumbs = useMemo(() => {
    if (shouldDisplayBreadcrumbs) {
      return subtitleNames.join(' - ').trim();
    }
    return null;
  }, [shouldDisplayBreadcrumbs, subtitleNames]);

  const shouldDisplaySeparator = useMemo(
    () => shouldDisplayStartDate && shouldDisplayBreadcrumbs,
    [shouldDisplayStartDate, shouldDisplayBreadcrumbs],
  );
  const shouldDisplayInfoRow = useMemo(
    () => shouldDisplayStartDate || shouldDisplayBreadcrumbs,
    [shouldDisplayStartDate, shouldDisplayBreadcrumbs],
  );

  if (!shouldDisplay) {
    return null;
  }

  return (
    <Wrapper className="search-row-result">
      <RowWrapper $border={border}>
        <ResultWrapper>
          <Row className="row-title">
            <StyledSportIcon sportId={sportId} $isLast={!shouldDisplayFlagIcon} />
            {shouldDisplayFlagIcon ? <StyledFlagIcon flagId={flagId} /> : null}
            <Title>
              <FirstTitle>{title}</FirstTitle>
              {secondTitle ? (
                <>
                  &nbsp;:&nbsp;<SecondTitle>{secondTitle}</SecondTitle>
                </>
              ) : null}
            </Title>
          </Row>
          {shouldDisplayInfoRow ? (
            <Row className="row-meta">
              {shouldDisplayStartDate ? <StartDate>{formattedStartDate}</StartDate> : null}
              {shouldDisplaySeparator ? <Separator>•</Separator> : null}
              {shouldDisplayBreadcrumbs ? (
                <Breadcrumbs>
                  {subtitleFlag ? <FlagIcon flagId={subtitleFlag} /> : null}
                  {breadcrumbs}
                </Breadcrumbs>
              ) : null}
            </Row>
          ) : null}
        </ResultWrapper>

        {isLive ? <ResultLiveWrapper>{__('Live')}</ResultLiveWrapper> : null}
      </RowWrapper>
    </Wrapper>
  );
};

export default SearchDefaultSearchRowResult;
